import { useState, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import {
    PageBody, Row,
    Icon, Badge, Text, Input, Label, Button,
    Modal, ModalHeader, ModalBody, ModalFooter, useUI, TanTable
} from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { DepartmentSchemaCategoriesController, ORDINAL_SORT, SchemaCategoryController, SchemaCategorySearchController } from '@evertel/document'
import { APIDataDocumentSchemaCategory } from '@evertel/types'
import { SessionService } from '@evertel/session/SessionService'
import { CurrentUserController } from '@evertel/blue-user'
import { ColumnDef } from '@tanstack/react-table'
import { DocumentsTabs } from '../elements'
import { CategoryTemplatesModal } from './CategoryTemplatesModal'
import { ReorderCategoriesModal } from './ReorderCategoriesModal'

const SchemaCategories = observer(() => {

    const sessionService = useService(SessionService, [])
    const currentUserController = useService(CurrentUserController, [])

    const schemaCategorySearchController = useService(SchemaCategorySearchController, [sessionService?.selectedDepartmentId])
    const schemaCategoryController = useService(SchemaCategoryController, [sessionService?.selectedDepartmentId])
    // the reorder endpoint needs every category of the department, so it can't use the paginated search
    const departmentSchemaCategoriesController = useService(DepartmentSchemaCategoriesController, [sessionService?.selectedDepartmentId])

    const { addToast, isConfirmed } = useUI()

    const [selectedCategory, setSelectedCategory] = useState<APIDataDocumentSchemaCategory>({ name: '' })
    const [showCategoryFormModal, setShowCategoryFormModal] = useState(false)
    // category is kept after close so the modal keeps its content while fading out
    const [templatesModalCategory, setTemplatesModalCategory] = useState<APIDataDocumentSchemaCategory>()
    const [showTemplatesModal, setShowTemplatesModal] = useState(false)

    const onShowTemplates = (category: APIDataDocumentSchemaCategory) => {
        setTemplatesModalCategory(category)
        setShowTemplatesModal(true)
    }
    const [showReorderModal, setShowReorderModal] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        // initialize (withSchemas: the Contains column needs each category's templates)
        schemaCategorySearchController.init(sessionService?.selectedDepartmentId || 0, true)
        schemaCategoryController.init(sessionService?.selectedDepartmentId || 0)
        departmentSchemaCategoriesController.init(sessionService?.selectedDepartmentId || 0)
    }, [departmentSchemaCategoriesController, schemaCategoryController, schemaCategorySearchController, sessionService?.selectedDepartmentId])

    // both sources have to be refreshed: the table shows a page, the reorder modal needs
    // the complete set of ids or the reorder endpoint rejects it
    const refreshCategories = async () => {
        await departmentSchemaCategoriesController.fetchDepartmentSchemaCategories()
        schemaCategorySearchController.resetMeta()
        schemaCategorySearchController.debouncedSearch()
    }

    const onSaveOrder = async (categoryIds: number[]) => {
        await schemaCategoryController.reorder(categoryIds)
        await refreshCategories()
    }

    useEffect(() => {
        schemaCategorySearchController.debouncedSearch()
    }, [schemaCategorySearchController, schemaCategorySearchController.limit, schemaCategorySearchController.page, schemaCategorySearchController.sortBy, schemaCategorySearchController.sortOrder])

    const onSelectCategory = (category: APIDataDocumentSchemaCategory) => {
        setSelectedCategory(category)
        setShowCategoryFormModal(true)
    }

    const onClickCreateCategory = () => {
        setSelectedCategory({ name: '' })
        setShowCategoryFormModal(true)
    }

    const onSave = async () => {
        try {
            setIsLoading(true)

            if (selectedCategory?.id) {
                await schemaCategoryController.update(selectedCategory)
            } else {
                await schemaCategoryController.add(selectedCategory.name as string)
            }

            // close before refreshing: the refresh swaps out every table row, so focus has to
            // be handed back to the Rename button while that button still exists
            setIsLoading(false)
            setShowCategoryFormModal(false)
            await refreshCategories()
        } catch (error: any) {
            setIsLoading(false)
            setShowCategoryFormModal(false)
            addToast({
                color: 'danger',
                title: 'Oops!',
                message: error.message
            })
        }
    }

    const onDelete = async (id: number) => {
        const deleteIt = async () => {
            try {
                await schemaCategoryController.delete(id)
                await refreshCategories()
            } catch (error: any) {
                addToast({
                    color: 'danger',
                    title: 'Oops!',
                    message: error.message
                })
            }
        }

        const confirmed = await isConfirmed({
            title: 'Delete Category?',
            message: 'Are you sure you want to delete this category? This category will also be removed from any templates assigned to it, and those templates can still be found in the "All" category.',
            acceptButton: {
                label: 'Delete',
                color: 'danger'
            }
        })
        if (confirmed) {
            deleteIt()
        }
    }

    // 1-based position of each category in the agency's saved order. Derived from the complete
    // list rather than the raw `ordinal`: a never-reordered agency has every ordinal at 0 and
    // deletes leave gaps, so the persisted value isn't presentable on its own
    const positionById = useMemo(() => {
        const positions = new Map<number, number>()
        departmentSchemaCategoriesController.categories.forEach((category: APIDataDocumentSchemaCategory, index) => {
            positions.set(category.id as number, index + 1)
        })
        return positions
    }, [departmentSchemaCategoriesController.categories])

    const columns = useMemo<ColumnDef<APIDataDocumentSchemaCategory>[]>(() => [
        {
            id: ORDINAL_SORT,
            accessorFn: (category) => positionById.get(category.id as number),
            header: 'Order',
            size: 40,
            enableSorting: true,
            meta: { align: 'center' },
            cell: ({ getValue }) => (
                <Text tag="span">
                    {getValue<number | undefined>() ?? '—'}
                </Text>
            )
        },
        {
            accessorKey: 'name',
            header: 'Category name',
            size: 200,
            enableSorting: true,
            cell: ({ row }) => (
                <Text tag="span" bold>
                    {row.original.name}
                </Text>
            )
        },
        {
            id: 'contains',
            size: 50,
            header: 'Templates',
            cell: ({ row }) => (
                <Button
                    color="link"
                    size="sm"
                    className="p-0"
                    onClick={() => onShowTemplates(row.original)}
                    aria-label={`View templates in ${row.original.name}`}
                >
                    {row.original.documentSchemas?.filter(t => t.isEnabled).length ?? 0}
                </Button>
            )
        },
        {
            accessorKey: 'createdDate',
            header: 'Creation date',
            enableSorting: true,
            cell: ({ getValue }) => (
                <Text tag="span">
                    {moment(getValue() as string).format('ll')}
                </Text>
            )
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => (
                <Row valign="center">
                    <Button
                        color="link"
                        size="sm"
                        className="p-0 me-3"
                        onClick={() => onSelectCategory(row.original)}
                        aria-label={`Rename category ${row.original.name}`}
                    >
                        <Icon name="pencil" className="me-1" />
                        Rename
                    </Button>
                    <Button
                        color="link"
                        size="sm"
                        className="p-0 text-danger-emphasis"
                        onClick={() => onDelete(row.original.id as number)}
                        aria-label={`Delete category ${row.original.name}`}
                    >
                        <Icon name="xmark" className="me-1" />
                        Delete
                    </Button>
                </Row>
            )
        }
    ], [positionById])

    return (
        <PageBody
            flush
            className="documents-page">
            <div className="px-4 pt-4">
                {currentUserController.canManage && <DocumentsTabs />}
                <Row
                    align="between"
                    valign="center"
                    className="flex-wrap mb-3">
                    <div>
                        <Row valign="center">
                            <Text
                                tag="h3"
                                className="mb-0">
                                All Categories
                            </Text>
                            {!!schemaCategorySearchController.categoriesCount && (
                                <Badge
                                    color="primary"
                                    className="ms-2">
                                    {schemaCategorySearchController.categoriesCount}
                                </Badge>
                            )}
                        </Row>
                        <Text
                            tag="p"
                            color="muted"
                            className="mb-0 mt-1">
                            View, create, or delete categories to organize your agency&apos;s templates.
                        </Text>
                    </div>
                    <Row valign="center">
                        <Button
                            color="neutral"
                            className="me-2"
                            onClick={() => setShowReorderModal(true)}>
                            <Icon
                                name="list"
                                className="me-1"
                            />
                            Reorder
                        </Button>
                        <Button
                            color="primary"
                            onClick={onClickCreateCategory}>
                            Create Category
                        </Button>
                    </Row>
                </Row>
            </div>
            <div className="px-4 pb-4">
                <TanTable
                    remote={true}
                    data={schemaCategorySearchController.categories}
                    rowCount={schemaCategorySearchController.categoriesCount}
                    columns={columns}
                    loading={schemaCategorySearchController.isLoading}
                    options={{
                        // a third header click clears the column sort; the controller then falls back to
                        // the Order column, and TanTable re-syncs the header from sortName/sortOrder
                        enableSortingRemoval: true,
                        pageSize: schemaCategorySearchController.limit,
                        pageNumber: schemaCategorySearchController.page,
                        sortName: schemaCategorySearchController.sortBy,
                        sortOrder: schemaCategorySearchController.sortOrder,
                        onPageChange: (page) => schemaCategorySearchController.setPage(page),
                        onPageSizeChange: (pageSize) => schemaCategorySearchController.setLimit(pageSize),
                        onSortChange: (sortName, sortOrder) => schemaCategorySearchController.setSorting(sortName, sortOrder)
                    }}
                    aria-label="Template Categories list"
                />
            </div>

            <Modal
                visible={showCategoryFormModal}
                onClose={() => setShowCategoryFormModal(false)}
                size="md"
            >
                <ModalHeader
                    title={!selectedCategory?.id ? 'Create a category' : 'Rename category'}
                />
                <ModalBody>
                    <Label text="Category name :" htmlFor="categoryName" />
                    <Input
                        name="categoryName"
                        value={selectedCategory?.name || ''}
                        onChange={(e) => setSelectedCategory((prev) => ({
                            ...prev,
                            name: e.target.value
                        }))}
                    />
                </ModalBody>
                <ModalFooter align="right">
                    <Button
                        color="link"
                        onClick={() => setShowCategoryFormModal(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        loading={isLoading}
                        onClick={onSave}
                        disabled={!selectedCategory?.name?.trim()}
                    >
                        Save
                    </Button>
                </ModalFooter>
            </Modal>

            <CategoryTemplatesModal
                visible={showTemplatesModal}
                category={templatesModalCategory}
                onClose={() => setShowTemplatesModal(false)}
            />

            <ReorderCategoriesModal
                visible={showReorderModal}
                categories={departmentSchemaCategoriesController.categories}
                onSave={onSaveOrder}
                onClose={() => setShowReorderModal(false)}
            />

        </PageBody>
    )
})

export { SchemaCategories }
